import type { RouteLocationNormalized } from "vue-router";

export const useBreadcrumbs = () => {
  const t = useTranslator();
  const breadcrumbsStore = useBreadcrumbsStore();

  const segmentKey = (segment: string): string => {
    return segment.toLowerCase().replace(/-/g, "").replace("()", "").replace(/:/g, "");
  };

  const resolveIcon = (segment: string): string | undefined => {
    const key = segmentKey(segment);

    return BREADCRUMB_CONFIG[key]?.icon || undefined;
  };

  const resolveLabel = (segment: string): string => {
    const key = segmentKey(segment);

    if (BREADCRUMB_CONFIG[key]?.translationKey) return t(BREADCRUMB_CONFIG[key].translationKey);

    return segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, " ");
  };

  const generateBreadcrumbs = (route: RouteLocationNormalized): BreadcrumbItem[] => {
    const rawMatchedPath = route.matched[route.matched.length - 1]?.path || "";
    const rawFullPath = route.fullPath;
    const matchedSegments = rawMatchedPath.split("/").filter(Boolean);
    const pathSegments = rawFullPath.split("/").filter(Boolean);

    const breadcrumbs: BreadcrumbItem[] = [
      {
        label: resolveLabel("home"),
        to: { path: "/" },
        active: matchedSegments.length === 0,
        icon: BREADCRUMB_CONFIG.home.icon,
      },
    ];

    matchedSegments.reduce((acc, segment, index) => {
      const currentPath = `/${matchedSegments.slice(0, index + 1).join("/")}`;
      const currentFullPath = `/${pathSegments.slice(0, index + 1).join("/")}`;

      // Skip excluded routes
      if (EXCLUDED_ROUTES.includes(currentPath)) {
        return acc;
      }

      // Check if the segment is a dynamic route parameter
      const dynamicSegmentMatch = currentPath.match(/:([\w]+)(\(\))?/);
      const isDynamicSegment = dynamicSegmentMatch && dynamicSegmentMatch[0] == segment;
      const dynamicSegmentName = isDynamicSegment ? dynamicSegmentMatch[1] : undefined;

      const breadcrumbItem: BreadcrumbItem = {
        label: resolveLabel(segment),
        to: {
          path: currentFullPath,
          ...route.params,
        },
        active: index === pathSegments.length - 1,
        icon: resolveIcon(segment),
      };

      // If it's a dynamic segment, store the segment name
      if (isDynamicSegment) {
        breadcrumbItem.dynamicSegment = dynamicSegmentName;
      }

      breadcrumbs.push(breadcrumbItem);

      return acc;
    }, []);

    return breadcrumbs;
  };

  const setBreadcrumbs = (items: BreadcrumbItem[]) => {
    breadcrumbsStore.setBreadcrumbs(items);
  };

  const setDynamicLabel = (segment: string, label: string) => {
    breadcrumbsStore.setDynamicLabel(segment, label);
  };

  return {
    breadcrumbs: computed(() => breadcrumbsStore.items),
    generateBreadcrumbs,
    setBreadcrumbs,
    setDynamicLabel,
  };
};
